//#region Util Imports

import * as _ from 'underscore'
import * as moment from 'moment'

//#endregion

import { ToastHelper, LocalizationHelper, DataProvider, TypeHelper } from '../../helpers'
import { KeyValuePair } from '../../helpers/dictionary'

import { OpOperator, OpOperatorGroup, OpTechnicalPermission, OpOperatorTechnicalPermission } from '../../models/core'
import { Enums } from '../../models/common'
import { IOpObject } from '../../models/util/interfaces'

import { GridController } from '../../modules/common/grid/grid.controller'
import { OperatorGroupHelper } from '../../modules/operator-group/operator-group-helper'

import { GRID_SERVICE_NAME, GridService } from '../grid'


//#region Public Types

export const OPERATOR_GROUP_TECHNICAL_PERMISSIONS_SERVICE_NAME = 'OperatorGroupTechnicalPermissionsService'
export const OPERATOR_GROUP_TECHNICAL_PERMISSIONS_SERVICE_PROVIDER_NAME = 'OperatorGroupTechnicalPermissionsServiceProvider'

//#endregion

export class OperatorGroupTechnicalPermissionsService {

    //#region Consts

    private static get VALIDATION_ITEMS_LIMIT(): number { return 3 }
    private static get VALIDATION_UNMATCHED_LIMIT(): number { return 3 }
    private static get SEPARATION_MARK(): string { return ': ' }
    private static get MORE_SIGN(): string { return '...' }
    private static get TAB_SIGN(): string { return '&nbsp;&nbsp;&nbsp;&nbsp;' }

    //#endregion


    //#region Ctor

    constructor(private gridService: GridService) { }

    //#endregion


    //#region Validation

    public createValidationMessageString(items: KeyValuePair<IOpObject[]>[], objects: IOpObject[]) {
        let messageString: string = TypeHelper.emptyString
        items.some((item: KeyValuePair<IOpObject[]>, index: number) => {

            if (index < OperatorGroupTechnicalPermissionsService.VALIDATION_ITEMS_LIMIT) {
                const name: string = _.find(objects, ((object: IOpObject) => object.idObject === item.key)).toString()
                messageString += name + TypeHelper.space + '(' + item.value.length + ')' + OperatorGroupTechnicalPermissionsService.SEPARATION_MARK + TypeHelper.HTMLNewLine
                item.value.some((unmatched: IOpObject, index: number) => {

                    if (index < OperatorGroupTechnicalPermissionsService.VALIDATION_UNMATCHED_LIMIT) {
                        messageString += OperatorGroupTechnicalPermissionsService.TAB_SIGN + unmatched.toString() + TypeHelper.commaSeparator + TypeHelper.HTMLNewLine
                    } else {
                        messageString += OperatorGroupTechnicalPermissionsService.TAB_SIGN + OperatorGroupTechnicalPermissionsService.MORE_SIGN + TypeHelper.HTMLNewLine
                        return true
                    }
                })
            } else {
                messageString += OperatorGroupTechnicalPermissionsService.MORE_SIGN
                return true
            }
        })

        return messageString
    }

    public displayValidationMessageInGridDetails(items: KeyValuePair<IOpObject[]>[], grid: GridController<IOpObject>): void {
        grid.dxGrid.option("masterDetail", {
            enabled: true,
            template: (container, options) => {
                const id = TypeHelper.getId(options.data)

                items.forEach((item: KeyValuePair<IOpObject[]>) => {
                    if (item.key === id) {
                        item.value.forEach((unmatched: IOpObject) => {
                            container.append($('<div><span>' + unmatched.toString() + '</span></div>'))
                        })
                    }
                })
            },
            autoExpandAll: true
        })
        grid.dxGrid.option("onRowPrepared", (e) => {
            let id: number
            if (e.rowType === "data") {
                id = TypeHelper.getId(e.data)
            }

            items.forEach(item => {
                if (item.key === id) {
                    const row = $(e.rowElement)
                    row.css('background-color', '#F7484E')
                    row.children('td').css('background-color', 'inherit')
                }
            })
        })
        grid.dxGrid.expandAll(-1)
    }

    public saveTechnicalPermissionToGroup(dataProvider: DataProvider, moduleName: Enums.SimaxModuleName, referenceObject: OpOperatorGroup, added: any[], removed: any[]): JQueryPromise<OpOperatorGroup> {
        const newTechnicalPermissions: OpTechnicalPermission[] = added.map((newTechnicalPermissionData: any) => {
            const technicalPermission: OpTechnicalPermission = new OpTechnicalPermission(null)

            technicalPermission.idTechnicalPermission = newTechnicalPermissionData.id
            technicalPermission.opState = Enums.OpChangeState.New

            return technicalPermission
        })

        const removedTechnicalPermissions: OpTechnicalPermission[] = removed.map((removedTechnicalPermissionData: OpTechnicalPermission) => {
            const technicalPermission: OpTechnicalPermission = new OpTechnicalPermission(null)

            technicalPermission.idTechnicalPermission = removedTechnicalPermissionData.idTechnicalPermission
            technicalPermission.opState = Enums.OpChangeState.Delete

            return technicalPermission
        })

        const removedTechnicalPermissionsIds: number[] = removedTechnicalPermissions.map(removedTechnicalPermission => removedTechnicalPermission.idTechnicalPermission)

        const objectToSave: OpOperatorGroup = new OpOperatorGroup(null)

        if (referenceObject != null && referenceObject.technicalPermissions != null) {
            const loadedTechnicalPermissions: OpTechnicalPermission[] = referenceObject.technicalPermissions
                .filter((technicalPermission: OpTechnicalPermission) => {
                    return !_.contains(removedTechnicalPermissionsIds, technicalPermission.idTechnicalPermission)
                })
                .map((loadedTechnicalPermission: OpTechnicalPermission) => {
                    const technicalPermission: OpTechnicalPermission = new OpTechnicalPermission(null)

                    technicalPermission.idTechnicalPermission = loadedTechnicalPermission.idObject
                    technicalPermission.opState = Enums.OpChangeState.Loaded

                    return technicalPermission
                })

            objectToSave.idOperatorGroup = referenceObject.idOperatorGroup
            objectToSave.opState = Enums.OpChangeState.Modified
            objectToSave.technicalPermissions = [...loadedTechnicalPermissions, ...newTechnicalPermissions, ...removedTechnicalPermissions]
        }

        return OperatorGroupHelper.saveOperatorGroup(dataProvider, objectToSave, moduleName)
    }

    public getOperatorsGridMasterDetailTemplateFunction(currentOperators: OpOperator[]): (container: any, options: any) => void {
        return (container, options) => {
            const operator: OpOperator = _.find(currentOperators, (operator: OpOperator) => operator.idOperator === options.data.id)
            container.append($('<div>'))

            if (operator.operatorTechnicalPermissions.length > 0) {
                operator.operatorTechnicalPermissions
                    .filter((operatorTechnicalPermission: OpOperatorTechnicalPermission) => {
                        return operatorTechnicalPermission.endTime == null || moment(operatorTechnicalPermission.endTime).isAfter(moment())
                    })
                    .sort((firstElement: OpOperatorTechnicalPermission, secondElement: OpOperatorTechnicalPermission) => firstElement.technicalPermission.toString().localeCompare(secondElement.technicalPermission.toString()))
                    .forEach((technicalPermission: OpOperatorTechnicalPermission, index: number) => {
                        if (index !== operator.operatorTechnicalPermissions.length - 1) {
                            container.append($('<span>' + technicalPermission.technicalPermission.toString() + TypeHelper.commaSeparator + '</span>'))
                        } else {
                            container.append($('<span>' + technicalPermission.technicalPermission.toString() + '</span>'))
                        }
                    })
            } else {
                container.append($('<span>' + LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoAssignedTechnicalPermissions) + '</span>'))
            }
            container.append($('</div>'))
        }
    }

    public validateOnSaveTechnicalPermissionToOperatorGroup(newTechnicalPermissions: any[], operators: OpOperator[], gridEdit: GridController<OpTechnicalPermission>): boolean {
        const unsuitableOperators: KeyValuePair<OpOperator[]>[] = []
        let technicalPermissions: _._Chain<OpTechnicalPermission>

        if (operators.length > 0) {
            technicalPermissions = _.chain(newTechnicalPermissions)
                .map((newTechnicalPermission: any) => this.gridService.getSourceObjectFromGridObject<OpTechnicalPermission>(newTechnicalPermission, Enums.ReferenceType.IdTechnicalPermission))
                .forEach((newTechnicalPermission: OpTechnicalPermission) => {
                    const okOperators: OpOperator[] = operators.filter((operator: OpOperator) => {
                        return !operator.operatorTechnicalPermissions.some((technicalPermission: OpOperatorTechnicalPermission) => {
                            return technicalPermission.technicalPermission.idTechnicalPermission === newTechnicalPermission.idObject
                        })
                    })

                    if (_.size(okOperators) > 0) {
                        unsuitableOperators.push(
                            new KeyValuePair<OpOperator[]>(newTechnicalPermission.idObject, okOperators)
                        )
                    }
                })
        }

        if (_.size(unsuitableOperators) > 0) {
            this.displayValidationMessage(
                unsuitableOperators,
                technicalPermissions.value(),
                LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SaveTechnicalPermissionToOperatorGroupValidationError) + ":",
                gridEdit
            )
            return false
        }
        return true
    }

    private displayValidationMessage(items: KeyValuePair<IOpObject[]>[], objects: IOpObject[], name: string, gridEdit: GridController<IOpObject>): void {
        const validationMessageString: string = this.createValidationMessageString(items, objects)
        ToastHelper.showErrorWithContent(name, validationMessageString)
        this.displayValidationMessageInGridDetails(items, gridEdit)
    }

    public createOperatorGroupTechnicalPermissionsSummary(operators: OpOperator[]): string {
        const availableTechnicalPermissionsArray: string[] = []

        if (operators != null && operators.length > 0) {
            operators.forEach((operator: OpOperator) => {
                if (
                    operator != null
                    && operator.operatorTechnicalPermissions != null
                    && operator.operatorTechnicalPermissions.length > 0
                ) {
                    operator.operatorTechnicalPermissions.forEach((operatorTechnicalPermission: OpOperatorTechnicalPermission) => {
                        if (
                            operatorTechnicalPermission != null
                            && operatorTechnicalPermission.technicalPermission != null
                        ) {
                            availableTechnicalPermissionsArray.push(operatorTechnicalPermission.technicalPermission.toString())
                        }
                    })
                }
            })
        }

        return _.uniq(availableTechnicalPermissionsArray).sort((firstElement: string, secondElement: string) => firstElement.localeCompare(secondElement)).join(TypeHelper.commaSeparator)
    }

    //#endregion
}

export class OperatorGroupTechnicalPermissionsServiceProvider implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [GRID_SERVICE_NAME, function (gridService: GridService) {
        return new OperatorGroupTechnicalPermissionsService(gridService)
    }]

    //#endregion
} 